/**
 * The share link's two ends on the device (`D257`, `D258`, `D260`) — PURE.
 *
 * - The SENDING end draws an opaque ref per card that carries a link
 *   ({@link newShareRef}) and writes it onto the server's base
 *   ({@link withLink}). While the server answers no base, no ref is drawn and
 *   nothing is written: the card carries the brand alone.
 * - The RECEIVING end reads `?ref=` once at boot ({@link landingRef}), keeps
 *   it for {@link REF_TTL_MS} ({@link liveRef}), and sends it with the
 *   sign-in code, where a NEW account turns it into `signup_from_link`.
 */
import {
  SHARE_REF_PARAM,
  type ShareRef,
  shareLinkFor,
  shareRef,
} from "@repo/contract";
import type { StoryImage } from "./story-image";

/** Crockford's base 32, lowercase: no i, l, o or u to misread. */
const ALPHABET = "0123456789abcdefghjkmnpqrstvwxyz";

/** Twelve characters: 60 bits, enough that two shares never meet. */
export const REF_LENGTH = 12;

/** A landing older than this is not what brought the account. */
export const REF_TTL_MS = 30 * 86_400_000;

/**
 * A fresh ref from `random` bytes — `crypto.getRandomValues` in the app,
 * which, unlike `crypto.randomUUID`, exists on the http live-reload origin
 * too (`T-174`).
 */
export function newShareRef(
  random: (bytes: Uint8Array<ArrayBuffer>) => Uint8Array = (bytes) =>
    crypto.getRandomValues(bytes)
): ShareRef {
  const bytes = random(new Uint8Array(new ArrayBuffer(REF_LENGTH)));
  let ref = "";
  for (const byte of bytes) {
    ref += ALPHABET[byte % ALPHABET.length];
  }
  return ref;
}

/** The card with its link written on, or untouched while links are off. */
export function withLink(
  image: StoryImage,
  base: string | null,
  ref: string | null
): StoryImage {
  if (base === null || ref === null) {
    return image;
  }
  return { ...image, link: shareLinkFor(base, ref) };
}

/** `?ref=` of a landing URL's search, when it is a ref and nothing else. */
export function landingRef(search: string): ShareRef | null {
  const value = new URLSearchParams(search).get(SHARE_REF_PARAM);
  if (value === null) {
    return null;
  }
  const parsed = shareRef.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** The search with `ref` taken out, so the router never sees it. */
export function withoutRef(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(SHARE_REF_PARAM);
  const rest = params.toString();
  return rest === "" ? "" : `?${rest}`;
}

export interface StoredRef {
  /** Epoch ms of the landing. */
  readonly at: number;
  readonly ref: ShareRef;
}

/** The stored landing, if it is well formed and still recent. */
export function liveRef(raw: string | null, now: number): ShareRef | null {
  if (raw === null) {
    return null;
  }
  try {
    const value = JSON.parse(raw) as Partial<StoredRef>;
    const parsed = shareRef.safeParse(value.ref);
    if (
      !parsed.success ||
      typeof value.at !== "number" ||
      now - value.at > REF_TTL_MS ||
      value.at > now
    ) {
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}
